import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { categoriesApi } from '../../../api/categories.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const EMPTY = { kind: 'species', slug: '', name: '', description: '', parentId: '', order: '', active: 'true' };

export function CategoryFormModal({ open, onClose, initial, defaultKind }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();

  const parentsQuery = useQuery({
    queryKey: ['categories', 'parents', defaultKind || (initial && initial.kind) || 'species'],
    enabled: open,
    queryFn: () => categoriesApi.list({ kind: defaultKind || (initial && initial.kind) || 'species', limit: 100 })
  });

  const parentOptions = useMemo(() => {
    const items = (parentsQuery.data && parentsQuery.data.items) || [];
    const list = items
      .filter((c) => !initial || String(c._id) !== String(initial._id))
      .map((c) => ({ value: c._id, label: c.name }));
    return [{ value: '', label: 'Nessuno' }, ...list];
  }, [parentsQuery.data, initial]);

  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    if (initial) {
      reset({
        kind: initial.kind || 'species',
        slug: initial.slug || '',
        name: initial.name || '',
        description: initial.description || '',
        parentId: (initial.parentId && (initial.parentId._id || initial.parentId)) || '',
        order: initial.order !== undefined && initial.order !== null ? String(initial.order) : '',
        active: String(initial.active !== false)
      });
    } else {
      reset({ ...EMPTY, kind: defaultKind || 'species' });
    }
  }, [open, initial, defaultKind, reset]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        kind: values.kind,
        slug: values.slug,
        name: values.name,
        description: values.description || undefined,
        parentId: values.parentId || null,
        active: values.active === 'true'
      };
      if (values.order !== '' && values.order !== undefined) payload.order = Number(values.order);
      if (editing) delete payload.kind;
      for (const k of Object.keys(payload)) if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      return editing ? categoriesApi.update(initial._id, payload) : categoriesApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Categoria aggiornata.' : 'Categoria creata.');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? 'Modifica categoria' : 'Nuova categoria'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="category-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="category-form" className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <Select label="Tipo" disabled={editing} error={errors.kind?.message} {...register('kind', { required: true })}>
          <option value="species">Specie</option>
          <option value="breed">Razza</option>
          <option value="productCategory">Categoria prodotto</option>
        </Select>
        <Input
          label="Slug"
          hint="minuscole, cifre e trattini"
          disabled={editing}
          error={errors.slug?.message}
          {...register('slug', editing ? {} : {
            required: 'Slug richiesto',
            pattern: { value: /^[a-z0-9](-?[a-z0-9])*$/, message: 'formato non valido' }
          })}
        />
        <Input label="Nome" error={errors.name?.message} {...register('name', { required: 'Nome richiesto' })} />
        <Select label="Categoria padre" options={parentOptions} error={errors.parentId?.message} {...register('parentId')} />
        <Input type="number" label="Ordine" min={0} error={errors.order?.message} {...register('order')} />
        <Select label="Stato" error={errors.active?.message} {...register('active')}>
          <option value="true">Attiva</option>
          <option value="false">Disattivata</option>
        </Select>
        <div className="sm:col-span-2">
          <Textarea label="Descrizione" rows={2} error={errors.description?.message} {...register('description')} />
        </div>
      </form>
    </Modal>
  );
}
